import { getChurchById } from "@/api/churches";
import { RecurringSchedules } from "@/features/services/components/RecurringSchedules";
import { getServiceContexts } from "@/api/authorization";
import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { createServiceOccurrence, createServiceSchedule, getServiceOccurrences } from "@/api/services";
import { Button } from "@/components/ui/button";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { PageContainer, SearchBar } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ScheduleServiceDialog } from "@/features/services/components/ScheduleServiceDialog";
import { ServicesCalendar } from "@/features/services/components/ServicesCalendar";
import { calendarPeriod } from "@/features/services/calendar";
import type { ServiceCreationRequest } from "@/types/service";

export function ServicesPage() {
  const { churchId } = useAuth();
  const queryClient = useQueryClient();
  const churchQuery = useQuery({ queryKey: ["church", churchId], queryFn: () => getChurchById(churchId!), enabled: !!churchId });

  const [params, setParams] = useSearchParams();
  const contextsQuery = useQuery({ queryKey: ["service-contexts", churchId], queryFn: () => getServiceContexts(churchId!), enabled: !!churchId });
  const contexts = contextsQuery.data ?? [];
  const requestedScope = params.get("ministry_id");
  const context = contexts.find(c => (c.ministry_id ?? "regular") === requestedScope) ?? contexts[0];
  const ministryId = context?.ministry_id ?? null;
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [serviceType, setServiceType] = useState("");
  const [status, setStatus] = useState("");
  const period = useMemo(() => calendarPeriod(month), [month]);
  const [scheduleDialogOpen, setScheduleDialogOpen] =
    useState(false);
  const [scheduleError, setScheduleError] = useState<
    string | null
  >(null);

  const servicesQuery = useQuery({
    queryKey: ["service-occurrences", churchId, ministryId, period.starts_from, period.starts_before],
    enabled: !!churchId && !!context,
    queryFn: () => getServiceOccurrences(churchId!, ministryId, period),
  });

  const scheduleMutation = useMutation({
    mutationFn: async (request: ServiceCreationRequest) => request.frequency === "ONE_TIME"
      ? createServiceOccurrence(churchId!, { ...request.occurrence, ministry_id: ministryId })
      : createServiceSchedule(churchId!, { ...request.schedule, ministry_id: ministryId }),

    onSuccess: async () => {
      setScheduleError(null);
      setScheduleDialogOpen(false);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            "service-occurrences",
            churchId,
          ],
        }),
        queryClient.invalidateQueries({ queryKey: ["service-schedules", churchId] }),
        queryClient.invalidateQueries({
          queryKey: ["services", churchId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["dashboard", churchId],
        }),
      ]);


    },

    onError: (error) => {
      setScheduleError(
        getErrorMessage(
          error,
          "Unable to schedule service."
        )
      );
    },
  });

  const services = servicesQuery.data ?? [];
  const filteredServices = services.filter(service =>
    (!serviceType || service.service_type === serviceType) &&
    (!status || service.status === status) &&
    `${service.title} ${service.location ?? ""}`.toLowerCase().includes(search.trim().toLowerCase())
  );
  const serviceTypes = [...new Set(["WORSHIP", "BIBLE_STUDY", "PRAYER", "YOUTH", "SPECIAL", ...services.map(s => s.service_type)])];

  if (contextsQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (contextsQuery.isError || servicesQuery.isError || !context) {
    return (
      <PageContainer>
        <QueryErrorState
          title="Unable to load services"
          description="MinistryIQ could not load your service schedule. Check your connection and try again."
          onRetry={() => { void contextsQuery.refetch(); void servicesQuery.refetch(); }}
        />
      </PageContainer>
    );
  }

  async function handleScheduleService(request: ServiceCreationRequest) {
    setScheduleError(null);
    await scheduleMutation.mutateAsync(request);
  }

  return (
    <PageContainer>
      {context?.is_youth_church && <Link className="mb-4 block text-indigo-300" to="/youth">{context.name} operations</Link>}
      <label className="mb-5 block text-sm text-slate-300">Service context
        <select aria-label="Service context" className="ml-3 rounded border border-slate-700 bg-slate-900 p-2" value={ministryId ?? "regular"} onChange={e => { setParams({ ministry_id: e.target.value }); setSearch(""); setServiceType(""); setStatus(""); setScheduleDialogOpen(false); }}>
          {contexts.map(c => <option key={c.ministry_id ?? "regular"} value={c.ministry_id ?? "regular"}>{c.ministry_id ? c.name : "Main Church"}</option>)}
        </select>
      </label>
      <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-indigo-950/25 px-7 py-8 shadow-xl shadow-black/20">
        <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-400">
                Services
              </p>

              {servicesQuery.isFetching && (
                <p className="text-xs font-medium text-indigo-300">
                  Refreshing services...
                </p>
              )}
            </div>

            <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Plan services and prepare attendance
            </h1>

            <p className="mt-3 max-w-2xl text-base leading-7 text-slate-300">
              Create a one-time service or a recurring weekly schedule. Open any occurrence to prepare attendance.
            </p>
          </div>

          <Button
            disabled={!context.can_manage_services}
            onClick={() => {
              setScheduleError(null);
              setScheduleDialogOpen(true);
            }}
          >
            <span className="flex items-center gap-2">
              <Plus size={18} />
              Create service
            </span>
          </Button>
        </div>
      </section>

      <section className="mt-7 space-y-5" aria-label="Services calendar">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-white" aria-live="polite">{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</h2>
            <p className="mt-1 text-sm text-slate-400">Times shown in {Intl.DateTimeFormat().resolvedOptions().timeZone}. Scroll sideways on small screens.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Previous month">Previous</Button>
            <Button variant="secondary" onClick={() => setMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>Today</Button>
            <Button variant="secondary" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Next month">Next</Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SearchBar value={search} placeholder="Search services..." onChange={setSearch}/>
          <select aria-label="Service type" className="rounded-lg border border-slate-700 bg-slate-900 p-2 text-slate-200" value={serviceType} onChange={e => setServiceType(e.target.value)}>
            <option value="">All service types</option>{serviceTypes.map(type => <option key={type} value={type}>{formatLabel(type)}</option>)}
          </select>
          <select aria-label="Service status" className="rounded-lg border border-slate-700 bg-slate-900 p-2 text-slate-200" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All statuses</option>{["SCHEDULED", "COMPLETED", "CANCELLED"].map(value => <option key={value} value={value}>{formatLabel(value)}</option>)}
          </select>
        </div>
        {servicesQuery.isLoading ? <p role="status" className="py-12 text-center text-slate-400">Loading calendar...</p> : <>
          <ServicesCalendar days={period.days} month={month} services={filteredServices} contextName={context.ministry_id ? context.name : "Main Church"}/>
          {filteredServices.length === 0 && <p className="text-sm text-slate-400">No services in this period match your filters. Navigate to another month or schedule a service.</p>}
        </>}
      </section>

      <RecurringSchedules churchId={churchId!} ministryId={ministryId} canManage={context.can_manage_services} timezone={churchQuery.data?.timezone} />

      <ScheduleServiceDialog
        creationContext={ministryId ?? "main"}
        contextName={context.ministry_id ? context.name : "Main Church"}
        timezone={churchQuery.data?.timezone}
        open={scheduleDialogOpen}
        isSubmitting={scheduleMutation.isPending}
        errorMessage={scheduleError}
        onClose={() => {
          if (!scheduleMutation.isPending) {
            setScheduleDialogOpen(false);
            setScheduleError(null);
          }
        }}
        onSubmit={handleScheduleService}
      />
    </PageContainer>
  );
}


function formatLabel(value: string) {
  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function getErrorMessage(
  error: unknown,
  fallback: string
) {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (
      typeof response?.data?.detail === "string"
    ) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}


